import { useMemo, type ReactNode } from "react";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";

import { createApplicationInsightsConsoleProxy } from "../../utils";

import { ApplicationInsightsContext } from "./application-insights.context";

const GLOBAL_KEY = "__packsize_application_insights__";

const getGlobalInstance = (): ApplicationInsightsSingleton | null => window[GLOBAL_KEY] ?? null;

const setGlobalInstance = (
	appInsights: ApplicationInsights,
	connectionString: string | undefined,
	applicationName: string,
	applicationVersion: string,
): void => {
	window[GLOBAL_KEY] = {
		appInsights,
		connectionString,
		applicationName,
		applicationVersion,
	};
};

export const getApplicationInsightsSingleton = (): ApplicationInsights => {
	const instance = getGlobalInstance();

	if (instance === null) {
		console.debug(
			"[@packsize/application-monitoring | ApplicationInsights] No initialized instance found, returning console proxy.",
		);
		return createApplicationInsightsConsoleProxy();
	}

	return instance.appInsights;
};

const initializeApplicationInsights = (
	connectionString: string | undefined,
	applicationName: string,
	applicationVersion: string,
): ApplicationInsights => {
	const existing = getGlobalInstance();

	if (
		existing !== null &&
		existing.connectionString === connectionString &&
		existing.applicationName === applicationName &&
		existing.applicationVersion === applicationVersion
	) {
		console.debug(
			`[@packsize/application-monitoring | ApplicationInsights] Reusing existing instance for "${applicationName}" with version "${applicationVersion}".`,
		);
		return existing.appInsights;
	}

	if (Boolean(connectionString?.trim()) === false) {
		console.debug(
			`[@packsize/application-monitoring | ApplicationInsights] No connection string provided for "${applicationName}" with version "${applicationVersion}", using console proxy.`,
		);
		return createApplicationInsightsConsoleProxy();
	}

	console.debug(
		`[@packsize/application-monitoring | ApplicationInsights] Initializing new instance for "${applicationName}" with version "${applicationVersion}".`,
	);

	const reactPlugin = new ReactPlugin();

	const instance = new ApplicationInsights({
		config: {
			connectionString,
			extensions: [reactPlugin],
			enableAutoRouteTracking: true,
			autoTrackPageVisitTime: true,
			enableRequestHeaderTracking: true,
			enableResponseHeaderTracking: true,
		},
	});

	instance.loadAppInsights();

	instance.addTelemetryInitializer((envelope) => {
		envelope.tags = envelope.tags ?? {};

		envelope.tags["ai.cloud.role"] = applicationName;
		envelope.tags["ai.application.ver"] = applicationVersion;
	});

	setGlobalInstance(instance, connectionString, applicationName, applicationVersion);

	console.debug(
		`[@packsize/application-monitoring | ApplicationInsights] Instance initialized and registered for "${applicationName}".`,
	);

	return instance;
};

interface ApplicationInsightsProviderProps {
	connectionString: string | undefined;
	applicationName: string;
	applicationVersion: string;
	children: ReactNode;
}

export const ApplicationInsightsProvider: React.FC<ApplicationInsightsProviderProps> = ({
	connectionString,
	applicationName,
	applicationVersion,
	children,
}) => {
	const instance = useMemo(
		() => initializeApplicationInsights(connectionString, applicationName, applicationVersion),
		[connectionString, applicationName, applicationVersion],
	);

	console.debug(
		`[ApplicationInsightsProvider] Providing instance for "${applicationName}" with version "${applicationVersion}".`,
		{ instance },
	);

	return <ApplicationInsightsContext.Provider value={instance}>{children}</ApplicationInsightsContext.Provider>;
};
